import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, BookOpen } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const { t } = useLanguage();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    document.title = t("payment.successTitle");
  }, [t]);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser, t]);

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <div className="text-slate-500">
          <Loader2 size={40} className="mx-auto animate-spin text-teal-600" />
          <p className="mt-6 font-mono text-sm">{t("payment.verifying")}</p>
        </div>
      )}

      {state === "paid" && (
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <CheckCircle2 size={64} className="mx-auto text-teal-600" />
          <h1 className="mt-6 font-serif font-bold text-3xl sm:text-4xl text-slate-900">{t("payment.successTitle")}</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">{t("payment.successBody")}</p>
          <Link to="/wiki">
            <Button data-testid="go-to-wiki-btn" className="mt-8 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-7 h-11">
              <BookOpen size={17} className="mr-2" /> {t("payment.goToWiki")}
            </Button>
          </Link>
        </motion.div>
      )}

      {state === "timeout" && (
        <div>
          <Loader2 size={44} className="mx-auto text-amber-500" />
          <h1 className="mt-6 font-serif font-bold text-2xl text-slate-900">{t("payment.successTitle")}</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">{t("payment.timeout")}</p>
          <Link to="/wiki" className="mt-6 inline-block text-teal-700 underline">{t("payment.backToWiki")}</Link>
        </div>
      )}

      {state === "failed" && (
        <div>
          <XCircle size={60} className="mx-auto text-rose-500" />
          <h1 className="mt-6 font-serif font-bold text-2xl text-slate-900">{t("payment.failedTitle")}</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">{t("payment.failedBody")}</p>
          <Link to="/wiki" className="mt-6 inline-block text-teal-700 underline">{t("payment.backToWiki")}</Link>
        </div>
      )}
    </div>
  );
}
